import {Component, OnDestroy} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Store} from '@ngrx/store';
import {Config, ConfigService, Mode} from './config/service/config.service';
import {merge, Subject, takeUntil} from 'rxjs';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {distinctUntilChanged, filter, first, map} from 'rxjs/operators';
import {ContextConfigService} from './config/service/context-config.service';
import {EmbeddedService} from './embedded/embedded.service';
import {UserAnnouncementEntitiesActions} from './store/user-announcement-entities';
import {Theme, ThemeService} from '@adnova/jf-ng-components';
import {FaviconService} from './config/service/favicon.service';
import {PortalConfigService} from './config/service/portal-config.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent
  implements OnDestroy {

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private titleService: Title,
    private configService: ConfigService,
    private contextConfigService: ContextConfigService,
    private oidcSecurityService: OidcSecurityService,
    private embeddedService: EmbeddedService,
    private store: Store,
    private themeService: ThemeService,
    private faviconService: FaviconService,
    private portalConfigService: PortalConfigService,
  ) {
    // info: update title
    this.configService.loaded$
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe(value => {
        this.updateTitle(value);
      });

    // info: apply theme based on shell mode from config
    this.configService.loaded$
      .pipe(
        map(config => config.mode),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(mode => {
        if (mode && Object.values<string>(Theme).includes(mode)) {
          this.themeService.setTheme(mode as Theme);
        }
      });

    this.portalConfigService.mode$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(mode => {
        this.faviconService.setForMode(mode);
      });

    // info: observe auth (standalone mode)
    const authenticated = this.oidcSecurityService.isAuthenticated$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(value => value.isAuthenticated),
        first(),
      );
    // info: observe embedded mode, external auth
    const embedded = this.embeddedService.embedded$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(value => value),
        first(),
      );
    // info join conditions, first wins
    const shouldLoad = merge(authenticated, embedded)
      .pipe(
        takeUntil(this.unsubscribe$),
        first(),
      );
    // info: trigger context data load when auth is present
    this.contextConfigService.loadConfig(shouldLoad)
      .subscribe();

    // INFO: Im Gewerbeportal entfallen Info-Pop-ups und Neuigkeiten-Archiv
    // (SHEL-81) - das Laden wird daher gar nicht erst angestoßen, damit
    // Marketing-Backend und Kampagnenverwaltung unverändert bleiben.
    shouldLoad
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (!this.portalConfigService.isGewerbeportal) {
          this.store.dispatch(UserAnnouncementEntitiesActions.loadCurrent());
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Setzt den Browser-Tab-Titel passend zum Portal.
   *
   * Der Titel wird bewusst vollständig neu gesetzt statt angehängt - so kann
   * sich der Environment-Zusatz bei erneuten Konfigurations-Emissionen nicht
   * mehrfach anhängen.
   */
  private updateTitle(
    config: Config,
  ): void {
    const baseTitle = this.portalTitle(config?.mode);

    const environment = config?.base?.environment;
    if (!environment || environment === 'production' || environment.length === 0) {
      this.titleService.setTitle(baseTitle);
      return;
    }
    this.titleService.setTitle(`${baseTitle} --${environment.toUpperCase()}--`);
  }

  /**
   * Unbekannte Modus-Werte fallen gemäß API-Vertrag auf "Just Farming" zurück.
   */
  private portalTitle(mode: Mode | undefined): string {
    return mode === 'gewerbeportal' ? 'Gewerbeportal' : 'Just Farming';
  }
}
